import React, { useState, useMemo } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { formatPriceWithUnit } from "../../utils/formatPrice";
import "../../css/models-common.css";
import "../../css/chart-main.css";
import "../../css/sector-materials.css";
import { useSectors, useSectorProducts } from "../../hooks";
import MiniChartSVG from "../../component/models/MiniChartSVG";
import placeholderImage from "../../assets/images/placeholder.jpg";
import { translateSectorLabel } from "../../utils/sectorLabel";

const SectorModels = () => {
  const { sectorId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const sectorName =
    translateSectorLabel(location.state?.sectorName, i18n.resolvedLanguage || i18n.language) ||
    t("charts.sector.defaultName");

  // Fetch all sectors to get the sector image
  const { data: sectors } = useSectors({
    enabled: true,
    cache: true,
    cacheTTL: 30 * 60 * 1000, // 30 minutes
  });

  // Fetch products for this sector
  const { data: sectorProductsData, loading: productsLoading, error: productsError } = useSectorProducts(sectorId);

  // Find current sector data to get image
  const currentSector = useMemo(() => {
    if (!sectors || !sectorId) return null;
    return sectors.find((s) => s.id === parseInt(sectorId));
  }, [sectors, sectorId]);

  // Get sector image with fallback
  const sectorImage = currentSector?.image || placeholderImage;

  // Transform API data to match expected format
  const modelsData = useMemo(() => {
    if (!sectorProductsData) return { lists: [] };

    // API returns { lists: [{ id, name, products: [...] }] }
    return {
      lists: sectorProductsData.lists?.map(list => ({
        id: list.id,
        name: list.name,
        models: list.products?.map(product => ({
          id: product.id,
          name: product.name,
          price: product.last_price_value
            ? formatPriceWithUnit(product.last_price_value, product.last_price_currency, product.last_price_unit)
            : '',
          date: product.last_price_date
            ? (() => {
                const [year, month, day] = product.last_price_date.split('-');
                return `${day}.${month}.${year}`;
              })()
            : '',
          chartData: product.chart_data || [],
        })) || [],
      })) || [],
    };
  }, [sectorProductsData]);

  const [expandedLists, setExpandedLists] = useState([0]); // First list expanded by default

  const handleModelClick = (modelId) => {
    navigate(`/models/model/${modelId}`);
  };

  const toggleList = (listIndex) => {
    setExpandedLists((prev) =>
      prev.includes(listIndex)
        ? prev.filter((idx) => idx !== listIndex)
        : [...prev, listIndex]
    );
  };

  return (
    <div className="sector-materials-page">
      {/* Sector Header with Image */}
      <div className="sector-header">
        <div
          className="sector-header-image"
          style={{ backgroundImage: `url(${sectorImage})` }}
        >
          <div className="sector-header-content">
            <h1 className="sector-header-title">
              {sectorName}
            </h1>
          </div>
        </div>
      </div>

      {/* Model Lists */}
      <div className="materials-lists">
        {productsLoading ? (
          <div className="loading-state">
            <p>{t('models.sector.loading')}</p>
          </div>
        ) : productsError ? (
          <div className="error-state">
            <p>Error loading models: {productsError.message}</p>
          </div>
        ) : modelsData &&
        modelsData.lists &&
        modelsData.lists.length > 0 ? (
          modelsData.lists.map((list, listIndex) => (
            <div key={listIndex} className="material-list-section">
              <div
                className="material-list-header"
                onClick={() => toggleList(listIndex)}
              >
                <h3>{list.name}</h3>
                <button className="toggle-icon">
                  {expandedLists.includes(listIndex) ? "−" : "+"}
                </button>
              </div>

              {expandedLists.includes(listIndex) && (
                <div className="material-list-content">
                  {list.models && list.models.length > 0 ? (
                    <div className="materials-grid models-cards-grid">
                      {list.models.map((model) => (
                        <div
                          key={model.id}
                          className="material-card"
                          onClick={() => handleModelClick(model.id)}
                        >
                          {/* Price and Date - Top Right */}
                          <div className="material-header">
                            <div className="material-price">{model.price}</div>
                            <div className="material-date">{model.date}</div>
                          </div>

                          {/* Chart - Center, Full Width */}
                          <div className="material-chart-wrapper">
                            <MiniChartSVG data={model.chartData} uniqueId={model.id} color="#EB5757" />
                          </div>

                          {/* Model Name - Bottom Left */}
                          <div className="material-footer">
                            <div className="material-name">{model.name}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="empty-list">{t('models.sector.empty')}</div>
                  )}
                </div>
              )}
            </div>
          ))
        ) : (
          <div className="empty-state">
            <p>{t('models.sector.noModels')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SectorModels;
